import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import { DEFAULT_LANGUAGE, SUPPORT_LANGUAGES, USER_LANGUAGE_STORAGE_KEY } from "../../consts";
import useTenantOptions from "../../hooks/useTenantOptions";
import { broadcastTranslationReload, onTranslationReload } from "../../i18nBroadcaster";

import styles from "./LanguageSwitcherContainer.module.scss";

const MIN_LANGUAGE_OPTIONS = 2;
export interface LanguageSwitcherContainerProps {
    hasHeader?: boolean;
    locales?: string[];
};

// Decorator for the LanguageSwitcher component
const LanguageSwitcherContainer = ({ hasHeader, locales }: LanguageSwitcherContainerProps) => {

    const { i18n } = useTranslation();
    const [tenantOptions] = useTenantOptions();

    locales = tenantOptions?.locales ?? locales;

    const allowedLanguages = useMemo(
        () => SUPPORT_LANGUAGES.filter(lang => locales?.includes(lang.code)),
        [locales]
    );

    const getInitialLanguage = useCallback(() => {
        const stored = localStorage.getItem(USER_LANGUAGE_STORAGE_KEY);
        if (stored && allowedLanguages.some(lang => lang.code === stored)) {
            return stored;
        }

        return DEFAULT_LANGUAGE;
    }, [allowedLanguages, tenantOptions]);

    const [currentLanguage, setCurrentLanguage] = React.useState<string>(getInitialLanguage);

    onTranslationReload((lang: string) => {
        if (currentLanguage === lang)
            return;
        setCurrentLanguage(lang);
    });
    
    useEffect(() => {
        setCurrentLanguage(getInitialLanguage());
        i18n.changeLanguage(getInitialLanguage());
        i18n.reloadResources();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowedLanguages, i18n, tenantOptions, getInitialLanguage, i18n]);

    const handleLanguageChange = useCallback(
        (_e: React.SyntheticEvent, value: unknown) => {
            if (typeof value === "string" && allowedLanguages.some(lang => lang.code === value)) {
                setCurrentLanguage(value);
                i18n.changeLanguage(value);
                localStorage.setItem(USER_LANGUAGE_STORAGE_KEY, value);
                broadcastTranslationReload(value);
            }
        },
        [allowedLanguages, i18n, tenantOptions]
    );

    const languageOptions = useMemo(
        () => allowedLanguages.map(({ code, name }) => ({ value: code, label: name })),
        [allowedLanguages]
    );

    // Render nothing if only one language is available
    if (languageOptions.length < MIN_LANGUAGE_OPTIONS) return (<></>);

    return (
        <div className={classnames({ [styles.containerSwitcher]: !hasHeader })}>
            <div className={classnames({ [styles.boxExternalWithoutLogin]: !hasHeader })}>
                <LanguageSwitcher
                    value={currentLanguage}
                    options={languageOptions}
                    onChange={handleLanguageChange}
                    width={100}
                    dropDownWidth={hasHeader ? 200 : 125}
                />
            </div>
        </div>
    );
};

export default LanguageSwitcherContainer;